import { computed, toValue, type MaybeRefOrGetter, type Ref } from 'vue';
import type { FilterItem } from '@/common/components/molecules/filterSearch/filterSearch.type';

/**
 * 그리드 row 데이터에서 컬럼별 고유 값 목록을 추출하는 composable.
 * ModernGrid 내장 FilterSearch 및 외부 FilterSearch 모두에서 사용 가능.
 *
 * @param rows - 원본 row 데이터 (필터 적용 전 전체 데이터)
 * @param filterItems - FilterSearch의 filterItems
 * @param childrenKey - 트리 구조일 경우 자식 행 배열의 키 이름 (일반 구조: 빈 문자열 또는 생략). Ref/computed 전달 가능.
 */
export const useAutoValueItems = ({
  rows,
  filterItems,
  childrenKey = '',
}: {
  rows: Ref<Record<string, any>[]>;
  filterItems: Ref<FilterItem[]>;
  childrenKey?: MaybeRefOrGetter<string>;
}) => {
  return computed(() => {
    const resolvedChildrenKey = toValue(childrenKey);
    const flattenRows = (rowList: Record<string, any>[]): Record<string, any>[] => {
      if (!resolvedChildrenKey) return rowList;
      const result: Record<string, any>[] = [];
      const stack = [...rowList];
      while (stack.length) {
        const row = stack.pop()!;
        result.push(row);
        const children = row[resolvedChildrenKey];
        if (Array.isArray(children)) {
          stack.push(...(children as Record<string, any>[]));
        }
      }
      return result;
    };

    const allRows = flattenRows(rows.value ?? []);
    const filterItemsList = filterItems.value ?? [];
    const result: Record<string, { id: string; name: string }[]> = {};

    for (const filterItem of filterItemsList) {
      if (filterItem.values?.items?.length) continue;
      const hasNumericOperators = filterItem.operators.some((op) =>
        ['>', '>=', '<', '<='].includes(op),
      );
      if (hasNumericOperators && filterItem.columnType !== 'stringNumber') continue;
      if (filterItem.defaultUnit) continue;

      const keyId = filterItem.key.id;
      const uniqueValues = new Set<string>();

      for (const row of allRows) {
        const cellValue = row[keyId];
        if (cellValue == null || cellValue === '') continue;

        if (Array.isArray(cellValue)) {
          cellValue.forEach((v) => {
            const resolved = typeof v === 'object' && v !== null && 'name' in v ? v.name : v;
            if (typeof resolved === 'string' && resolved !== '')
              uniqueValues.add(resolved.replace(/\s{2,}/g, ' '));
            else if (typeof resolved === 'number' || typeof resolved === 'boolean')
              uniqueValues.add(String(resolved));
          });
        } else if (typeof cellValue === 'string') {
          uniqueValues.add(cellValue.replace(/\s{2,}/g, ' '));
        } else if (typeof cellValue === 'number' || typeof cellValue === 'boolean') {
          uniqueValues.add(String(cellValue));
        } else if (typeof cellValue === 'object' && cellValue !== null) {
          Object.keys(cellValue).forEach((key) => {
            if (key !== '') uniqueValues.add(key.replace(/\s{2,}/g, ' '));
          });
        }
      }

      if (uniqueValues.size > 0) {
        result[keyId] = Array.from(uniqueValues)
          .sort((a, b) => a.localeCompare(b))
          .map((v) => ({ id: v, name: v }));
      }
    }

    return result;
  });
};
